/**
 * Однократный reload при падении dynamic import — защита от version skew:
 * после деплоя открытая страница может запросить ленивый чанк, которого на
 * сервере уже нет (хэш сменился). Reload уводит навигацию в свежий precache/билд.
 *
 * Без сети reload не лечит, а зацикливается (фидбек владельца 24.09.2026:
 * первый визит, сеть пропала сразу после карты — страница перезагружалась
 * каждые ~1,3 с). Прогрев «Поехали»/«Укрытия» падал без сети, reload из кеша
 * service worker снова рисовал карту, движок карты из кеша импортировался
 * удачно и сбрасывал предохранитель, прогрев падал снова. Поэтому:
 *  - без сети reload не делаем вовсе — отдаём честную ошибку вызывающему;
 *  - reload не чаще раза в RELOAD_COOLDOWN_MS на вкладку, и удачный импорт
 *    предохранитель НЕ сбрасывает: сеть бывает «есть, но не ходит»
 *    (navigator.onLine лжёт), и тогда второй провал — тоже честная ошибка.
 */
const RELOAD_GUARD_KEY = 'chunk-reloaded-at'

/** Следующий деплой раньше, чем через 10 минут, — не наш случай. */
export const RELOAD_COOLDOWN_MS = 10 * 60_000

/** Решение о reload (чистая функция — для тестов). */
export function shouldReloadForChunk(
  now: number,
  lastReloadAt: number | null,
  online: boolean,
): boolean {
  if (!online) return false
  return lastReloadAt === null || now - lastReloadAt >= RELOAD_COOLDOWN_MS
}

/** sessionStorage недоступен (приватный режим, запрет cookies) — считаем, что reload не было. */
function readLastReloadAt(): number | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY)
    const at = raw === null ? Number.NaN : Number(raw)
    return Number.isFinite(at) ? at : null
  } catch {
    return null
  }
}

/** Не смогли записать метку — reload не делаем: без неё нечем разорвать цикл. */
function writeLastReloadAt(at: number): boolean {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(at))
    return true
  } catch {
    return false
  }
}

export async function importChunk<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load()
  } catch (error) {
    const now = Date.now()
    if (!shouldReloadForChunk(now, readLastReloadAt(), navigator.onLine)) throw error
    if (!writeLastReloadAt(now)) throw error
    location.reload()
    // Страница уходит в перезагрузку — промис намеренно не резолвится.
    return new Promise<never>(() => undefined)
  }
}
